import { create } from 'zustand';
import api from '../api';

export type NotesData = Awaited<ReturnType<typeof api.listNotes>>;
export type NoteItem = NotesData['notes'][number];

/**
 * The member's Notes inbox (quick notes + memo highlights), loaded once and shared by the Notes page,
 * the capture dialog's idea picker and the dashboard line. Mutations return the fresh inbox, which
 * replaces the cache; the page applies optimistic edits on top.
 */
interface NotesStore {
  data: NotesData | null;
  loading: boolean;
  error: string | null;
  load: (force?: boolean) => Promise<NotesData | null>;
  set: (data: NotesData) => void;
  patch: (fn: (d: NotesData) => NotesData) => void;
}

let inflight: Promise<NotesData | null> | null = null;

export const useNotes = create<NotesStore>((set, get) => ({
  data: null,
  loading: false,
  error: null,
  load: (force = false) => {
    if (!force && get().data) return Promise.resolve(get().data);
    if (inflight) return inflight;
    set({ loading: true, error: null });
    inflight = api
      .listNotes()
      .then((d) => {
        set({ data: d, loading: false });
        return d;
      })
      .catch((e: any) => {
        set({ loading: false, error: String(e?.message ?? 'Could not load your notes.') });
        return null;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  },
  set: (data) => set({ data }),
  patch: (fn) => {
    const d = get().data;
    if (d) set({ data: fn(d) });
  },
}));

/** Re-read the inbox in the background (after a capture, or a highlight change on a memo). */
export function refreshNotes() {
  if (useNotes.getState().data) void useNotes.getState().load(true);
}

/** Open the capture dialog from anywhere ("Make it an idea" in the inbox). */
export function openCapture(detail: { mode: 'idea' | 'note'; text?: string; fromNoteId?: string }) {
  window.dispatchEvent(new CustomEvent('ed:open-capture', { detail }));
}
